// frontend/src/components/SearchInputWithPredictions.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaSearch, FaTimes } from 'react-icons/fa';
import './SearchInputWithPredictions.css'; // Link to its dedicated CSS file

const DEBOUNCE_DELAY = 300; // Time in milliseconds to wait after typing stops before fetching predictions

/**
 * A reusable search input component with predictive text suggestions.
 * It handles input changes, debouncing, filtering predictions from a provided list,
 * and displaying them in a dropdown.
 *
 * @param {Object} props - The component props.
 * @param {string} props.searchTerm - The current value of the search input.
 * @param {function(string): void} props.setSearchTerm - Function to update the search term state.
 * @param {function(string): void} props.onSearch - Callback function to execute when a search is submitted (e.g., Enter key or prediction click).
 * @param {Array<Object>} props.allSpices - The complete list of products (spices) to use for generating predictions.
 * @param {React.Ref<HTMLInputElement>} [props.inputRef] - Optional ref to pass to the underlying input element, useful for focusing.
 * @param {string} props.placeholder - The placeholder text for the search input.
 * @param {boolean} [props.isMobile=false] - A flag to apply mobile-specific styling or behavior (e.g., show a close 'X' icon).
 * @param {function(): void} [props.onClose] - Optional callback for an explicit close action (e.g., for mobile search bar's 'X' button).
 */
const SearchInputWithPredictions = ({
    searchTerm,
    setSearchTerm,
    onSearch,
    allSpices,
    inputRef,
    placeholder,
    isMobile = false,
    onClose
}) => {
    // State to hold the filtered search suggestions
    const [predictions, setPredictions] = useState([]);
    // State to control the visibility of the predictions dropdown
    const [showPredictions, setShowPredictions] = useState(false);
    // Ref to store the debounce timer ID
    const debounceTimerRef = useRef(null);
    // Ref for the entire container, used to detect clicks outside
    const containerRef = useRef(null);

    /**
     * Filters the `allSpices` array to generate search predictions.
     * @param {string} query - The current search input query.
     */
    const fetchPredictions = useCallback((query) => {
        // If query is empty or no spices are provided, clear predictions and hide dropdown
        if (!query.trim() || !allSpices || allSpices.length === 0) {
            setPredictions([]);
            setShowPredictions(false);
            return;
        }

        const lowerCaseQuery = query.toLowerCase();
        // Filter spices based on name or category containing the query
        const filtered = allSpices.filter(spice =>
            spice.name.toLowerCase().includes(lowerCaseQuery) ||
            (spice.category && typeof spice.category === 'string' && spice.category.toLowerCase().includes(lowerCaseQuery))
            // You can add more fields to search here (e.g., spice.description)
        );

        // Limit the number of predictions displayed to keep the UI clean
        setPredictions(filtered.slice(0, 7));
        // Show predictions only if there are matching results
        setShowPredictions(filtered.length > 0);
    }, [allSpices]); // Re-memoize if the list of allSpices changes

    /**
     * Handles changes in the search input field.
     * It updates the search term immediately and debounces the prediction fetching.
     * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event.
     */
    const handleInputChange = useCallback((e) => {
        const value = e.target.value;
        setSearchTerm(value); // Update the search term state immediately

        // Clear any existing debounce timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Set a new debounce timer to fetch predictions after a delay
        debounceTimerRef.current = setTimeout(() => {
            fetchPredictions(value);
        }, DEBOUNCE_DELAY);
    }, [setSearchTerm, fetchPredictions]);

    /**
     * Handles the 'Enter' key press in the search input.
     * Triggers the `onSearch` callback and clears/hides predictions.
     * @param {React.KeyboardEvent<HTMLInputElement>} e - The keyboard event.
     */
    const handleKeyPress = useCallback((e) => {
        if (e.key === 'Enter') {
            if (searchTerm.trim()) {
                onSearch(searchTerm); // Trigger the actual search action
            }
            // Clear and hide predictions immediately after submission
            setPredictions([]);
            setShowPredictions(false);
            // Clear any pending debounce timer to prevent delayed prediction updates
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        }
    }, [searchTerm, onSearch]);

    /**
     * Handles clicking on a prediction item.
     * Sets the search term to the clicked prediction, triggers search, and hides predictions.
     * @param {string} predictionName - The name of the clicked prediction.
     */
    const handlePredictionClick = useCallback((predictionName) => {
        setSearchTerm(predictionName); // Set the search term to the selected prediction
        onSearch(predictionName); // Trigger the search for the selected item
        setPredictions([]); // Clear predictions
        setShowPredictions(false); // Hide predictions
    }, [onSearch, setSearchTerm]);

    /**
     * Effect to detect clicks outside the component and close the predictions dropdown.
     */
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setShowPredictions(false);
            }
        };

        // Add event listener when component mounts
        document.addEventListener('mousedown', handleClickOutside);
        // Clean up event listener when component unmounts
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    /**
     * Handles the explicit close action (e.g., 'X' button on mobile search bar).
     * Clears search term and predictions, then calls parent's onClose.
     */
    const handleCloseClick = () => {
        setSearchTerm('');
        setPredictions([]);
        setShowPredictions(false);
        if (onClose) {
            onClose(); // Call the parent's close handler
        }
    };

    return (
        <div className={`search-input-predictions-container ${isMobile ? 'mobile-mode' : 'desktop-mode'}`} ref={containerRef}>
            <input
                type="text"
                placeholder={placeholder}
                className={`search-input ${showPredictions ? 'active-predictions' : ''}`} // Add class when predictions are active
                value={searchTerm}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                ref={inputRef} // Pass the ref from parent to the actual input
                aria-label={placeholder}
                // Show predictions on focus if there's already text typed
                onFocus={() => { if (searchTerm.trim()) setShowPredictions(true); }}
            />
            {/* Render a close 'X' icon for mobile mode */}
            {isMobile && (
                <FaTimes className="mobile-search-close-icon" onClick={handleCloseClick} />
            )}
            {/* Render a search submit icon for desktop mode (if not a toggle) */}
            {!isMobile && (
                 <button className="search-submit-icon" onClick={() => {
                     if (searchTerm.trim()) onSearch(searchTerm); // Trigger search on icon click
                 }}>
                    <FaSearch />
                 </button>
            )}

            {/* Conditional rendering for the predictions dropdown */}
            {showPredictions && predictions.length > 0 && (
                <div className="predictions-dropdown">
                    {predictions.map(prediction => (
                        <div
                            key={prediction._id || prediction.name} // Use unique ID or name for key
                            className="prediction-item"
                            onClick={() => handlePredictionClick(prediction.name)}
                        >
                            {prediction.name}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SearchInputWithPredictions;
